import { useNavigate } from "react-router-dom";
import { Type } from "@shared/ui/Type";
import { Button } from "@shared/ui/Button";

export type Props = {
  lastHeartbeat?: string | null;
};

export function VideoUIAlreadyInRoomError({ lastHeartbeat }: Props) {
  const navigate = useNavigate();

  const formatLastHeartbeat = (timestamp: string | null) => {
    if (!timestamp) return "Unknown";
    
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      
      if (diffMinutes < 1) return "Just now";
      if (diffMinutes === 1) return "1 minute ago";
      if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
      
      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours === 1) return "1 hour ago";
      if (diffHours < 24) return `${diffHours} hours ago`;
      
      return date.toLocaleString();
    } catch {
      return "Unknown";
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[100dvh] gap-6 px-4">
      <div className="text-center max-w-md">
        <div className="mb-4">
          <div className="w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <Type variant="h4" className="text-orange-600 mb-2">
            Already in Room
          </Type>
          <Type variant="body1" className="text-gray-700 mb-4">
            You're already connected to this video room on another device.
          </Type>
          <Type variant="body2" className="text-gray-500">
            {lastHeartbeat && (
              <>
                Last activity: {formatLastHeartbeat(lastHeartbeat)}
              </>
            )}
          </Type>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <Type variant="body2" className="text-blue-800 text-center">
            To join on this device, please leave the room on your other device first.
          </Type>
        </div>
      </div>
      
      <div className="flex gap-3">
        <Button 
          onClick={() => navigate("/dashboard")} 
          variant="secondary"
        >
          Back to Dashboard
        </Button>
        <Button 
          onClick={() => window.location.reload()} 
          variant="primary"
        >
          Try Again
        </Button>
      </div>
    </div>
  );
}

export default VideoUIAlreadyInRoomError;
